'use client';
/**
 * Assistant « Nouvelle demande » — FENÊTRE SUPERPOSÉE (lot GED-2).
 *
 * Il vivait en colonne collante DANS l'écran des demandes, et l'écran Documents
 * y menait par `go('req')` : on quittait le catalogue — ses filtres, son tri, la
 * ligne qu'on était en train de lire — pour déposer une demande, et il fallait
 * tout refaire au retour. La demande naît pourtant d'un constat fait AILLEURS
 * (« ce mode opératoire n'existe pas »), c'est-à-dire au moment précis où l'on
 * regarde le fonds documentaire.
 *
 * D'où la fenêtre : elle se pose PAR-DESSUS l'écran appelant, qui reste monté et
 * intact derrière elle. Trois exigences la rendent utilisable au clavier :
 *   — Échap ferme (`useEscape`) ;
 *   — le focus part sur le premier champ à l'ouverture et REVIENT sur l'élément
 *     qui l'a ouverte à la fermeture (sans quoi la tabulation repart du haut de
 *     la page, et un lecteur d'écran perd le fil) ;
 *   — annuler n'écrit RIEN : le seul appel réseau est `createDemande`, déclenché
 *     par le bouton d'envoi. Fermer, c'est démonter un brouillon local.
 *
 * `contexte` permet à l'appelant de pré-remplir ce qu'il sait déjà — le
 * catalogue filtré sur « Biochimie » sait que la demande portera très
 * probablement sur la biochimie. Ce n'est qu'une proposition : tout reste
 * modifiable dans la fenêtre.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGed } from '@/lib/store';
import { genCodePreview, procCodif, autoDiscCode, DEFAULT_SEGMENTS } from '@/lib/meta';
import { VOILE, I, card, inputStyle, useBusy, useEscape, Selecteur } from '@/components/ui';

import { Bouton } from '@/shared/Bouton';
/**
 * Rapprochement de doublons — VRAIE comparaison de contenu.
 *
 * L'ancien code prenait les 2 premiers documents de la catégorie sans rien
 * comparer et les annonçait « proches » : un garde-fou anti-doublon
 * entièrement factice (QA v2 BUG-10). On tokenise l'intitulé demandé et on le
 * confronte au titre + code de chaque document du fonds ; on ne retient que
 * ceux qui partagent réellement des termes significatifs.
 */
const STOP = new Set(['des', 'les', 'une', 'aux', 'par', 'pour', 'sur', 'dans', 'avec', 'and', 'the', 'documentaire', 'procedure', 'mode', 'operatoire', 'instruction', 'fiche']);
/**
 * Découpage en termes significatifs.
 *
 * ⚠️ On sépare AUSSI les lettres des chiffres à l'intérieur d'un mot :
 * « ISO15189 » donne « iso » + « 15189 ». Sans cela, la forme collée ne
 * partageait AUCUN terme avec « ISO 15189:2022 » — mesuré le 14/08/2026 sur le
 * fonds réel : « ISO 15189 v 2012 » trouvait bien la norme en vigueur, la même
 * demande écrite « ISO15189 v 2012 » ne trouvait rien. Or c'est précisément sur
 * les références normatives que l'on colle : ISO15189, EN14971, NF S97.
 *
 * Le reste est inchangé : accents retirés, minuscules, termes de moins de trois
 * lettres écartés, mots vides du domaine exclus.
 */
const toks = (s: string): Set<string> =>
  new Set((s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    // Frontière lettre↔chiffre : on insère une coupure avant de découper.
    .replace(/([a-z])(\d)/g, '$1 $2').replace(/(\d)([a-z])/g, '$1 $2')
    .split(/[^a-z0-9]+/).filter((t) => t.length >= 3 && !STOP.has(t)));

export function findDoublons(desc: string, docs: { code: string; titre: string; docType?: string; discipline?: string }[], type: string, disc: string): string[] {
  const q = toks(desc);
  if (q.size === 0) return [];
  const scored = docs.map((d) => {
    const cand = toks(`${d.titre} ${d.code}`);
    let inter = 0;
    q.forEach((t) => { if (cand.has(t)) inter++; });
    // Jaccard sur les termes significatifs + petit bonus même type/discipline.
    const union = new Set([...q, ...cand]).size || 1;
    const jaccard = inter / union;
    const affinite = (d.docType === type ? 0.05 : 0) + (disc && d.discipline === disc ? 0.05 : 0);
    return { code: d.code, inter, score: jaccard + (inter ? affinite : 0) };
  })
    // Il faut au moins 2 termes communs OU un recouvrement franc : sinon ce
    // n'est pas un doublon, c'est du hasard lexical.
    .filter((x) => x.inter >= 2 || x.score >= 0.34)
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, 3).map((x) => x.code);
}

export type ContexteDemande = { type?: string; discipline?: string; description?: string };

export function AssistantDemande({ contexte, onFerme }: { contexte?: ContexteDemande; onFerme: () => void }) {
  const { demandes, docs, settings, session, createDemande } = useGed();
  const { run: runDem, estOccupe: occupeDem } = useBusy();

  const types = useMemo(() => settings?.docTypes?.filter((t) => t.active) || [], [settings?.docTypes]);
  const discs = useMemo(() => settings?.disciplines?.filter((d) => d.active) || [], [settings?.disciplines]);
  const procs = useMemo(() => (settings?.processus || []).filter((p) => p.active), [settings?.processus]);
  const sites = useMemo(() => settings?.sites || [], [settings?.sites]);

  const [wizDesc, setWizDesc] = useState(contexte?.description || '');
  const [wizType, setWizType] = useState(contexte?.type || 'MO');
  const [wizDisc, setWizDisc] = useState(contexte?.discipline || '');
  const [wizProc, setWizProc] = useState('');
  const [wizSite, setWizSite] = useState('');

  // Segments retenus par le RQ : ils décident quels choix l'assistant présente.
  const segments = settings?.codification?.segments?.length ? settings.codification.segments : DEFAULT_SEGMENTS;

  // ---- Clavier : ouverture, Échap, retour du focus -------------------------
  // L'élément actif AVANT l'ouverture est mémorisé une seule fois, au montage :
  // c'est lui qui reprendra le focus à la fermeture, quelle qu'en soit la cause
  // (Échap, « Annuler », clic sur le voile, envoi réussi).
  const declencheurRef = useRef<HTMLElement | null>(null);
  const champRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    declencheurRef.current = document.activeElement as HTMLElement | null;
    champRef.current?.focus();
    return () => { declencheurRef.current?.focus?.(); };
  }, []);
  useEscape(!occupeDem(), onFerme);

  const wizCode = useMemo(() => {
    const discName = wizDisc || discs[0]?.name || '';
    const discObj = (settings?.disciplines || []).find((d) => d.name === discName);
    const procObj = procs.find((p) => p.id === wizProc) || procs[0];
    const siteName = wizSite || sites[0] || '';
    const codif = {
      ...(settings?.codification || {}),
      discCode: discObj?.code,
      processCode: procObj ? procCodif(procObj) : '',
      siteCode: siteName ? (settings?.codification?.siteCodes?.[siteName] || autoDiscCode(siteName).slice(0, 3)) : '',
    };
    // Séquentiel = max+1 sur le préfixe TYPE‑DISC réellement utilisé (constat QA
    // MAJ‑7) : l'ancien comptage (nb de docs du type, toutes disciplines) ne
    // reflétait pas la séquence par discipline et réattribuait un numéro déjà
    // pris dès qu'un document était supprimé.
    // Préfixe = le code complet moins le séquentiel (on retranche exactement la
    // longueur du padding : un abrégé de discipline peut finir par un chiffre).
    const pad = Math.max(1, Math.min(6, codif.seqPad ?? 3));
    const sample = genCodePreview(wizType, discName, 1, codif);
    const prefix = sample.slice(0, sample.length - pad);
    const used = [
      ...docs.map((d) => d.code),
      ...demandes.map((d) => d.propositions?.code),
    ].filter((c): c is string => !!c && c.startsWith(prefix));
    const max = used.reduce((m, c) => {
      const n = parseInt(c.slice(prefix.length), 10);
      return Number.isFinite(n) && n > m ? n : m;
    }, 0);
    return genCodePreview(wizType, discName, max + 1, codif);
  }, [wizType, wizDisc, wizProc, wizSite, docs, demandes, discs, procs, sites, settings]);

  // Rapprochement en direct : l'utilisateur voit les doublons potentiels PENDANT
  // qu'il saisit, pas après coup. Même fonction que celle enregistrée au submit.
  const wizDoublons = useMemo(
    /* Pas de repli sur la première discipline de la liste : il donnait un
       bonus d'affinité aux documents de « Biochimie » pour TOUTE demande dont
       la discipline n'était pas renseignée — un classement subtilement faux,
       d'autant plus trompeur qu'il ne se voit pas. Vide reste vide. */
    () => findDoublons(wizDesc, docs, wizType, wizDisc),
    [wizDesc, docs, wizType, wizDisc],
  );

  // Créer une demande : sans garde, un double clic déposait deux fois le même
  // enregistrement dans le dossier.
  const submitWiz = () => runDem('wiz', async () => {
    if (!wizDesc.trim()) return;
    const ok = await createDemande(wizDesc.trim(), {
      type: wizType, code: wizCode, titre: wizDesc.trim().slice(0, 90),
      /* ⚠️ Plus de repli sur la PREMIÈRE discipline de la liste. Il datait du
         temps où « Qualité » en était une : depuis la séparation du 14/08, il
         collait « Biochimie » à toute procédure d'achats — une famille
         biologique inventée, qui commande ensuite la signature. Vide veut dire
         vide, et c'est une réponse. */
      discipline: wizDisc, processus: wizProc || procs[0]?.id || '', site: wizSite || sites[0] || '',
      /* Un document EXTERNE n'a pas de rédacteur : il n'est pas rédigé ici.
         `redacteur` y garde le sens de « qui l'a enregistré », affiché comme
         tel sur la fiche ; vérificateur et approbateur restent vides, et le
         type ne les réclame plus (son circuit se réduit à la diffusion). */
      redacteur: session.key, verificateur: '', approbateur: '', perimetre: '', references: [], motsCles: [],
      doublons: wizDoublons,
    });
    // La fenêtre ne se ferme QUE sur écriture confirmée : un échec réseau qui
    // ferait disparaître la saisie serait pire que l'échec lui-même.
    if (ok) { setWizDesc(''); onFerme(); }
  });

  const occupe = occupeDem();
  const lbl: React.CSSProperties = { display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 5 };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="assist-dem-titre"
      style={{ position: 'fixed', inset: 0, background: VOILE, display: 'grid', placeItems: 'center', padding: 16, zIndex: 400 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ ...card, padding: 20, width: 'min(520px, 100%)', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-primary-active)' }}>Assistant guidé</div>
            <h2 id="assist-dem-titre" style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-strong)', margin: '4px 0 0' }}>Nouvelle demande de document</h2>
          </div>
          <Bouton taille="sm" onClick={onFerme} disabled={occupe} aria-label="Fermer l’assistant">
            <I n="x" s={13} />
          </Bouton>
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '8px 0 14px' }}>
          Rien n’est enregistré tant que vous n’avez pas soumis — fermer cette fenêtre n’écrit aucune demande.
        </p>

        <label style={lbl} htmlFor="assist-desc">Décrivez votre besoin</label>
        <textarea id="assist-desc" ref={champRef} value={wizDesc} onChange={(e) => setWizDesc(e.target.value)} rows={3}
          placeholder="Ex. « Mode opératoire pour l’ensemencement des prélèvements ORL… »"
          style={{ ...inputStyle, height: 'auto', padding: '10px 12px', resize: 'vertical', marginBottom: 10 }} />

        <label style={lbl} htmlFor="assist-type">Type proposé</label>
        <select id="assist-type" value={wizType} onChange={(e) => setWizType(e.target.value)} style={{ ...inputStyle, marginBottom: 10 }}>
          {types.map((t) => <option key={t.code} value={t.code}>{t.code} — {t.label}</option>)}
        </select>

        {/* ── LES DEUX AXES, ET CE QU'ILS DÉCIDENT (14/08/2026) ──────────────
            Le PROCESSUS est demandé À CHAQUE FOIS, qu'il entre ou non dans le
            code : c'est lui qui classe le document dans le système de
            management, et c'est lui qui commande la signature quand aucune
            discipline n'est portée (`controleHabilitation`). L'omettre laissait
            un document sans axe — donc signable par quiconque.

            La DISCIPLINE reste offerte, mais pour ce qu'elle est depuis la
            séparation : une famille BIOLOGIQUE, celle du catalogue du manuel de
            prélèvement. Un mode opératoire d'automate en porte une, une
            procédure d'achats n'en porte aucune — et « aucune » est une réponse,
            pas un oubli. C'est ce que le texte sous le champ dit. */}
        <span style={lbl}>Processus <span style={{ color: 'var(--red-600)' }}>*</span></span>
        <Selecteur value={wizProc} onChange={setWizProc} style={{ ...inputStyle, marginBottom: 10 }}>
          <option value="">—</option>
          {procs.map((p) => <option key={p.id} value={p.id}>{procCodif(p)} — {p.name}</option>)}
        </Selecteur>
        <span style={lbl}>Discipline — famille biologique</span>
        <Selecteur value={wizDisc} onChange={setWizDisc} style={{ ...inputStyle, marginBottom: 4 }}>
          <option value="">— aucune : ce document ne relève d’aucune famille —</option>
          {discs.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
        </Selecteur>
        <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
          À renseigner seulement si le document porte sur une famille d’analyses. Une procédure
          qualité, un document d’achats ou de métrologie n’en relève d’aucune.
        </p>
        {segments.includes('site') && sites.length > 1 && (
          <>
            <label style={lbl} htmlFor="assist-site">Site</label>
            <select id="assist-site" value={wizSite} onChange={(e) => setWizSite(e.target.value)} style={{ ...inputStyle, marginBottom: 10 }}>
              <option value="">—</option>
              {sites.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface-sunken)', border: '1px dashed var(--border-strong)', borderRadius: 'var(--radius-md)', padding: '9px 12px', marginBottom: 12 }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Code auto-généré</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700, color: 'var(--navy-900)', marginInlineStart: 'auto' }}>{wizCode}</span>
        </div>

        {/* Rapprochement RÉEL : n'apparaît que si l'intitulé partage des termes
            avec un document existant (avant, on affichait toujours 2 docs). */}
        {wizDesc.trim().length >= 4 && (
          <div style={{ fontSize: 12.5, borderRadius: 'var(--radius-md)', padding: '9px 12px', marginBottom: 12, background: wizDoublons.length ? '#FCF2E1' : 'var(--surface-sunken)', color: wizDoublons.length ? 'var(--amber-600)' : 'var(--text-muted)' }}>
            {wizDoublons.length
              ? <>⚠ Possible doublon — proche de <strong style={{ fontFamily: 'var(--font-mono)' }}>{wizDoublons.join(', ')}</strong>. Vérifier avant de soumettre.</>
              : 'Aucun document proche dans le fonds documentaire.'}
          </div>
        )}

        {/* Ordre normé du 10/08/2026 : « Annuler » puis l'action. Le principal
            garde son flex:1 — l'assistant tient sur une colonne étroite. */}
        <div style={{ display: 'flex', gap: 8 }}>
          <Bouton taille="sm" onClick={onFerme} disabled={occupe}>Annuler</Bouton>
          <Bouton variante="primaire" onClick={() => void submitWiz()} disabled={!wizDesc.trim() || occupe} style={{ flex: 1 }}>
            <I n="send" s={14} />{occupeDem('wiz') ? 'Envoi…' : 'Soumettre la demande'}
          </Bouton>
        </div>
      </div>
    </div>
  );
}
